// src/utils/mediaConversion/formatEta.ts

export type EtaTone = "calm" | "neutral";

export interface FormatEtaOptions {
  short?: boolean;
  tone?: EtaTone;
}

/**
 * Human-friendly ETA formatter for UI.
 *
 * Goals:
 * - No second-by-second jitter
 * - No false precision
 * - Calm, reassuring wording
 * - Reusable across panel / toast / notification
 */
export function formatHumanETA(
  seconds: number | null,
  opts: FormatEtaOptions = {}
): string | null {
  if (seconds == null || seconds <= 0) return null;

  const { short = false, tone = "calm" } = opts;

  // --- helpers ----------------------------------------------------
  const calm = {
    almost: short ? "almost done" : "almost done…",
    ltMinute: short ? "< 1 min" : "less than a minute left",
    oneMinute: short ? "1 min" : "about 1 minute left",
    minutes: (m: number) =>
      short ? `${m} min` : `about ${m} minutes left`,
    fewMinutes: short ? "few min" : "a few minutes left",
    longer: short ? "a while" : "this may take a bit longer",
  };

  const neutral = {
    almost: short ? "done" : "finishing",
    ltMinute: short ? "<1m" : "less than 1 minute remaining",
    oneMinute: short ? "1m" : "1 minute remaining",
    minutes: (m: number) =>
      short ? `${m}m` : `${m} minutes remaining`,
    fewMinutes: short ? "~5m" : "several minutes remaining",
    longer: short ? ">10m" : "more time remaining",
  };

  const dict = tone === "neutral" ? neutral : calm;

  // --- rules ------------------------------------------------------
  if (seconds < 20) return dict.almost;
  if (seconds < 60) return dict.ltMinute;

  const minutes = Math.round(seconds / 60);

  if (minutes <= 1) return dict.oneMinute;
  if (minutes < 5) return dict.minutes(minutes);
  if (minutes < 10) return dict.fewMinutes;

  return dict.longer;
}
